import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";

import { useLanguage } from "../../context/languageContext";
import Button from "../../components/button";
import { useAlreadyExists } from "../../components/confirmDialog";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Create or rename a pillar — the name and nothing else. Sub-pillars are added
 * on the card afterwards, so a new pillar is one field and one click.
 *
 * On edit the name comes from the row the page already has, so there's no
 * fetch-just-to-prefill round trip.
 */
const AddEditPopup = (props) => {
    const { adminInfo } = useLanguage();
    const showAlreadyExists = useAlreadyExists();
    const { isVisible, pillarId, pillar, handleCloseModal, onSuccess } = props;

    const isCreate = !pillarId;

    const [name, setName] = useState("");
    const [errors, setErrors] = useState({});
    const [isSubmit, setIsSubmit] = useState(false);

    useEffect(() => {
        if (!isVisible) return;
        setErrors({});
        setName(pillar?.name || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, pillarId]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            setErrors({ name: "Please enter a pillar name." });
            return;
        }

        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const url = isCreate
                ? `${API}/api/admin/pillar/add`
                : `${API}/api/admin/pillar/update/${pillarId}`;
            const { data: response } = await axios[isCreate ? "post" : "put"](
                url,
                { name: name.trim() },
                { headers }
            );

            if (response.status) {
                onSuccess();
                handleClose();
                toast.success(isCreate ? "Pillar created." : "Pillar updated.");
            } else {
                toast.error(response.message || "Could not save this pillar.");
            }
        } catch (error) {
            console.error("handleSubmit catch error::", error);
            if (!showAlreadyExists(error)) toast.error(error.response?.data?.error || "Could not save this pillar.");
        } finally {
            setIsSubmit(false);
        }
    };

    const handleClose = () => {
        handleCloseModal();
        setName("");
        setErrors({});
    };

    return (
        <Modal isOpen={isVisible} onRequestClose={handleClose} className="modal_body_sm" overlayClassName="modal_overlay">
            <div className="box_wrapper">
                <h4 className="fw-bold mb-3">{isCreate ? "Add Pillar" : "Edit Pillar"}</h4>

                <div className="modal_content_sm">
                    <div className="mb-3">
                        <label className="label">Pillar name:</label>
                        <input
                            className="form_control"
                            type="text"
                            placeholder="e.g. Mindset"
                            value={name}
                            autoFocus
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                        />
                        {errors.name && <p className="error-message d-block">{errors.name}</p>}
                        {isCreate && (
                            <small className="text-muted">
                                You'll add sub pillars to it from the card once it's created.
                            </small>
                        )}
                    </div>
                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>Cancel</button>
                    <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Saving ..." text={isCreate ? "Create" : "Update"} />
                </div>
            </div>
        </Modal>
    );
};

export default AddEditPopup;
